<template>
  <div class="card">
    <div
      class="card-header bg-transparent border-0 pb-0 d-flex justify-content-between align-items-start gap-3 flex-wrap"
    >
      <div>
        <h5 class="mb-1">{{ $t("forwards.overview.deals.title") }}</h5>
        <p class="text-body-tertiary fs-10 mb-0">
          {{ $t("forwards.overview.deals.description") }}
        </p>
      </div>
      <div v-if="showsCreateLinks" class="d-flex gap-2 flex-wrap">
        <RouterLink
          :to="{ name: 'AdminTransactionsNewForwardView' }"
          class="btn btn-sm btn-outline-primary"
        >
          {{ $t("forwards.overview.deals.new_deal") }}
        </RouterLink>
        <RouterLink
          :to="{ name: 'ForwardsPapDealNew' }"
          class="btn btn-sm btn-outline-primary"
        >
          {{ $t("forwards.overview.deals.new_pap_deal") }}
        </RouterLink>
      </div>
    </div>

    <div class="card-body">
      <div class="row g-2 align-items-end mb-3">
        <div class="col-12 col-md-3">
          <label class="eyebrow-label eyebrow-label--sm mb-1" :for="statusId">
            {{ $t("forwards.overview.deals.filters.status") }}
          </label>
          <MultiSelectComponent
            v-model="statuses"
            :options="statusOptions"
            :buttonId="statusId"
            :placeholder="$t('forwards.overview.deals.filters.status_all')"
            size="sm"
          />
        </div>
        <div class="col-12 col-md-3">
          <label class="eyebrow-label eyebrow-label--sm mb-1" :for="nameId">
            {{ $t("forwards.overview.deals.filters.name") }}
          </label>
          <input
            :id="nameId"
            v-model="nameInput"
            type="search"
            :maxlength="DEAL_FILTER_TEXT_MAX"
            class="form-control form-control-sm"
            :placeholder="
              $t('forwards.overview.deals.filters.name_placeholder')
            "
          />
        </div>
        <div class="col-12 col-md-3">
          <label class="eyebrow-label eyebrow-label--sm mb-1" :for="mandateId">
            {{ $t("forwards.overview.deals.filters.mandate") }}
          </label>
          <input
            :id="mandateId"
            v-model="mandateInput"
            type="search"
            :maxlength="DEAL_FILTER_TEXT_MAX"
            class="form-control form-control-sm"
            :placeholder="
              $t('forwards.overview.deals.filters.mandate_placeholder')
            "
          />
        </div>
        <div class="col-12 col-md-3">
          <label class="eyebrow-label eyebrow-label--sm mb-1" :for="companyId">
            {{ $t("forwards.overview.deals.filters.company") }}
          </label>
          <input
            :id="companyId"
            v-model="companyInput"
            type="search"
            :maxlength="DEAL_FILTER_TEXT_MAX"
            class="form-control form-control-sm"
            :placeholder="
              $t('forwards.overview.deals.filters.company_placeholder')
            "
          />
        </div>
      </div>

      <RetryableErrorNoteComponent
        v-if="errorMessage"
        class="mb-3"
        :message="errorMessage"
        @retry="emit('retry')"
      />

      <LoadingIndicatorComponent
        v-if="loading"
        :message="$t('forwards.overview.deals.loading')"
      />

      <DataTable
        v-else-if="!errorMessage"
        :value="decoratedRows"
        dataKey="deal_id"
        class="border rounded"
        sortField="name"
        :sortOrder="1"
        resizableColumns
        columnResizeMode="fit"
        @column-resize-end="onColumnResizeEnd"
      >
        <template #empty>
          <div class="text-center py-4 text-muted">
            {{ $t("forwards.overview.deals.empty") }}
          </div>
        </template>

        <Column
          field="name"
          :header="$t('forwards.overview.deals.columns.deal')"
          :style="columnStyle('deal')"
          sortable
        >
          <template #sorticon="{ sortOrder }">
            <FontAwesomeIcon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #body="{ data }">
            <RouterLink :to="dealRoute(data.deal_id)">
              {{ data.name }}
            </RouterLink>
            <ForwardsStatusBadgeComponent
              v-if="data.type === 'pay_as_produced'"
              status="pay_as_produced"
              class="ms-2"
            />
          </template>
        </Column>

        <Column
          field="buyer"
          :header="$t('forwards.overview.deals.columns.buyer')"
          :style="columnStyle('buyer')"
          sortable
        >
          <template #sorticon="{ sortOrder }">
            <FontAwesomeIcon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #body="{ data }">
            <span v-if="data.buyer">{{ data.buyer }}</span>
            <span v-else class="text-body-tertiary">
              {{ $t("forwards.overview.deals.no_buyer") }}
            </span>
          </template>
        </Column>

        <Column
          :header="$t('forwards.overview.deals.columns.quarters')"
          :style="columnStyle('quarters')"
        >
          <template #body="{ data }">
            {{ quartersLabel(data.quarters) }}
          </template>
        </Column>

        <Column
          field="volume_mwh"
          :header="$t('forwards.overview.deals.columns.volume')"
          :style="columnStyle('volume')"
        >
          <template #body="{ data }">
            <span v-if="data.volume_mwh !== null" class="text-nowrap">
              {{ formatNumberWithSpaces(data.volume_mwh) }}
            </span>
            <span v-else class="text-body-tertiary">
              {{ $t("forwards.common.unknown") }}
            </span>
          </template>
        </Column>

        <Column
          :header="$t('forwards.overview.deals.columns.price')"
          :style="columnStyle('price')"
        >
          <template #body="{ data }">
            <span v-if="data.price !== null" class="text-nowrap">
              {{ data.price }}
            </span>
            <span v-else class="text-body-tertiary">
              {{ $t("forwards.overview.deals.no_price") }}
            </span>
          </template>
        </Column>

        <Column
          :header="$t('forwards.overview.deals.columns.deliveries')"
          :style="columnStyle('deliveries')"
        >
          <template #body="{ data }">
            <span class="text-nowrap">
              {{
                $t("forwards.overview.deals.deliveries_of", {
                  done: data.deliveries_completed,
                  total: data.deliveries_total,
                })
              }}
            </span>
          </template>
        </Column>

        <Column
          field="status"
          :header="$t('forwards.overview.deals.columns.status')"
          :style="columnStyle('status')"
          sortable
        >
          <template #sorticon="{ sortOrder }">
            <FontAwesomeIcon
              :icon="getSortIcon(sortOrder)"
              class="pv-datatable-sort-icon"
            />
          </template>
          <template #body="{ data }">
            <div class="d-flex align-items-center gap-1 flex-wrap">
              <ForwardsStatusBadgeComponent :status="data.status" />
              <span
                v-if="data.is_stale"
                class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
              >
                {{
                  $t("forwards.overview.deals.stale", {
                    days: ageInDays(data.created_at),
                  })
                }}
              </span>
              <span
                v-if="data.openVolume"
                class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
              >
                {{ data.openVolume }}
              </span>
              <span
                v-if="data.drift"
                class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis"
              >
                {{ data.drift }}
              </span>
              <span
                v-if="data.is_infeasible"
                class="badge rounded-pill bg-warning-subtle text-warning-emphasis"
              >
                {{ $t("forwards.overview.deals.infeasible") }}
              </span>
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { usePersistedColumnWidths } from "@/composables/usePersistedColumnWidths";
import ForwardsStatusBadgeComponent from "@/forwards2/components/ForwardsStatusBadgeComponent.vue";
import RetryableErrorNoteComponent from "@/forwards2/components/RetryableErrorNoteComponent.vue";
import { volumeDriftLabel } from "@/forwards2/deals/dealBadges";
import { dealRoute } from "@/forwards2/deals/dealRoute";
import { forwardsErrorMessage } from "@/forwards2/forwardsApi";
import {
  DEAL_FILTER_TEXT_MAX,
  DEAL_STATUS_FILTERS,
  type DealRow,
  type DealStatus,
  type DealsQuery,
} from "@/forwards2/forwardsTypes";
import { shiftQuarterLabel, shortQuarterLabel } from "@/forwards2/periodUtils";
import { getSortIcon } from "@/utils/sortIcons";
import { refDebounced } from "@vueuse/core";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { computed, ref, useId, watch } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";

/**
 * The deals this account manages, with the filters that narrow them.
 *
 * The volume column is the deal's own stored volume, not the sum of its commitments — the two
 * differ while a deal is under-committed, and the stored figure is what was sold (plan §1.1). The
 * difference between them is the drift badge beside the status.
 *
 * Filters are owned by the caller through `v-model:filters` so they can live in the URL. The three
 * substring filters reach the model debounced: each change is a request and a history entry.
 */
const props = withDefaults(
  defineProps<{
    rows: DealRow[];
    loading?: boolean;
    error?: unknown;
    /** Staff are offered the create pages; a sales admin opens them read-only. */
    showsCreateLinks?: boolean;
  }>(),
  { loading: false, showsCreateLinks: false }
);

const emit = defineEmits<{
  retry: [];
}>();

const filters = defineModel<DealsQuery>("filters", { required: true });

const { t } = useI18n();

const FILTER_DEBOUNCE_MS = 350;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const OPEN_VOLUME_STATUSES: DealStatus[] = ["draft", "confirmed"];

const { columnStyle, onColumnResizeEnd } = usePersistedColumnWidths(
  "forwards2.managed-deals.v1",
  ["deal", "buyer", "quarters", "volume", "price", "deliveries", "status"]
);

const statusId = useId();
const nameId = useId();
const mandateId = useId();
const companyId = useId();

const nameInput = ref(filters.value.name);
const mandateInput = ref(filters.value.mandate);
const companyInput = ref(filters.value.company);

const debouncedName = refDebounced(nameInput, FILTER_DEBOUNCE_MS);
const debouncedMandate = refDebounced(mandateInput, FILTER_DEBOUNCE_MS);
const debouncedCompany = refDebounced(companyInput, FILTER_DEBOUNCE_MS);

interface DecoratedDealRow extends DealRow {
  /** Pre-rendered so the template asks once, rather than in both the `v-if` and the text. */
  drift: string | null;
  openVolume: string | null;
  price: string | null;
}

const decoratedRows = computed<DecoratedDealRow[]>(() =>
  props.rows.map((row) => ({
    ...row,
    drift: driftLabel(row),
    openVolume: openVolumeLabel(row),
    price: priceLabel(row),
  }))
);

const statuses = computed<DealStatus[]>({
  get: () => filters.value.status,
  set: (status) => {
    filters.value = { ...filters.value, status };
  },
});

const statusOptions = computed(() =>
  DEAL_STATUS_FILTERS.map((value) => ({
    value,
    label: t(`forwards.common.status.${value}`),
  }))
);

const errorMessage = computed(() => {
  if (props.error === null || props.error === undefined) return null;
  return (
    forwardsErrorMessage(props.error) ??
    t("forwards.overview.deals.load_failed")
  );
});

watch(
  [debouncedName, debouncedMandate, debouncedCompany],
  ([name, mandate, company]) => {
    if (
      name === filters.value.name &&
      mandate === filters.value.mandate &&
      company === filters.value.company
    ) {
      return;
    }
    filters.value = { ...filters.value, name, mandate, company };
  }
);

// The URL is the filters' home, so the back button and a pasted link both move them under us.
watch(filters, (next) => {
  if (next.name !== nameInput.value) nameInput.value = next.name;
  if (next.mandate !== mandateInput.value) mandateInput.value = next.mandate;
  if (next.company !== companyInput.value) companyInput.value = next.company;
});

/**
 * `Q2 26 — Q1 27` for a run of consecutive quarters, the single label for a lone one, runs joined by
 * commas (`Q1 26 — Q2 26, Q4 26`), a dash for none. The backend sends the labels sorted.
 */
/** One price when every priced delivery agrees, else the span from the lowest to the highest. */
const priceLabel = (row: DealRow): string | null => {
  if (row.price_min === null || row.price_max === null) return null;
  if (Number(row.price_min) === Number(row.price_max)) return row.price_min;
  return t("forwards.overview.deals.price_range", {
    min: row.price_min,
    max: row.price_max,
  });
};

const quartersLabel = (quarters: string[]): string => {
  if (quarters.length === 0) return t("forwards.common.none");
  return consecutiveRuns(quarters)
    .map((run) => {
      const first = shortQuarterLabel(run[0]);
      const last = shortQuarterLabel(run[run.length - 1]);
      return first === last ? first : `${first} — ${last}`;
    })
    .join(", ");
};

/** Sorted quarter labels split wherever the next quarter is not the one right after the last. */
const consecutiveRuns = (quarters: string[]): string[][] =>
  quarters.reduce<string[][]>((runs, quarter) => {
    const current = runs.at(-1);
    return current !== undefined &&
      shiftQuarterLabel(current[current.length - 1], 1) === quarter
      ? [...runs.slice(0, -1), [...current, quarter]]
      : [...runs, [quarter]];
  }, []);

/**
 * How far the deal's plan has drifted from the volume it sold — total planned against `volume_mwh`,
 * the same quantity the deal page badges (spec "deal-volume drift"; RV-overview-07).
 *
 * @returns the badge text, or null when there is nothing to report: a pay-as-produced deal has no
 * headline volume to drift from, and a plan that still matches what was sold is not drift.
 */
const driftLabel = (row: DealRow): string | null => {
  if (row.type !== "fixed_volume") return null;
  if (row.planned_mwh === null || row.volume_mwh === null) return null;

  const drift = row.planned_mwh - row.volume_mwh;
  if (drift === 0) return null;

  return volumeDriftLabel(t, drift);
};

/**
 * The volume the plan holds that no mandate stands behind yet (planned − committed).
 *
 * @returns the badge text, or null when nothing is open: only a pay-as-produced deal reports no
 * `committed_mwh` to subtract. A fixed-volume deal nobody has backed reports 0 and badges its whole
 * plan, which is the most under-committed a deal can be (RV2-overview-jobs-04). Only a live deal
 * can still be backed — a cancelled one released its commitments but keeps its slices — so the
 * badge follows `overview_service._open_slice_rows` and stays off every other status.
 */
const openVolumeLabel = (row: DealRow): string | null => {
  if (!OPEN_VOLUME_STATUSES.includes(row.status)) return null;
  if (row.planned_mwh === null || row.committed_mwh === null) return null;

  const open = row.planned_mwh - row.committed_mwh;
  if (open <= 0) return null;

  return t("forwards.common.deal_badges.under_committed", {
    mwh: formatNumberWithSpaces(open),
  });
};

const ageInDays = (createdAt: string): number => {
  const elapsed = Date.now() - new Date(createdAt).getTime();
  return Math.max(0, Math.floor(elapsed / MILLISECONDS_PER_DAY));
};
</script>
